/**
 * Keeping a customer's REAL contact details in their notes while the fields point
 * somewhere else.
 *
 * <h2>Why</h2>
 *
 * A migrating workshop tests the customer emails and texts by pointing a customer's
 * phone and email at its own. The real ones have to go somewhere it can see them and
 * get them back from — the workshop's own words were "save the old value to a note".
 * So the edit screen writes one line into the notes, in a shape it can read again,
 * and offers to put the values back.
 *
 * <h2>The line</h2>
 *
 * <pre>Real contact before testing (11 Sep 2026): phone 0415359944 · email x@y.com</pre>
 *
 * Human first — it is a note, and it reads as one — but fixed enough that
 * {@link findStashedContact} finds it again without guessing. A part that was empty
 * is written as "none", so a customer with no email restores to no email rather than
 * to the word "undefined".
 */

const PREFIX = 'Real contact before testing';
const LINE = new RegExp(`^${PREFIX} \\(([^)]*)\\): phone (.*?) · email (.*)$`, 'm');

/** What an absent part is written as, and read back from. */
const NONE = 'none';

// By hand, not toLocaleDateString: the same runtime writes "Sep" on one machine and
// "Sept" on another, and a line that is read back must be written the same way twice.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatDate = (date) =>
    `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;

/**
 * The notes with the real contact stashed on a new line at the end.
 *
 * <p>🔑 Refuses to stash twice. A second press would record the TEST details as the
 * "real" ones and lose the real ones for good, so if a line is already there the
 * notes come back unchanged.
 *
 * @param {{ phone?: string|null, email?: string|null, notes?: string|null }} current
 * @param {Date} [today]
 * @returns {string}
 */
export function stashContact({ phone, email, notes }, today = new Date()) {
    const existing = notes ?? '';
    if (findStashedContact(existing)) return existing;

    const line = `${PREFIX} (${formatDate(today)}): phone ${phone?.trim() || NONE} · email ${email?.trim() || NONE}`;
    return existing.trim() ? `${existing.trimEnd()}\n${line}` : line;
}

/**
 * The stashed contact, if the notes hold one.
 *
 * @param {string|null|undefined} notes
 * @returns {{ phone: string, email: string, stashedOn: string, line: string }|null}
 *          phone/email are '' where the line says none
 */
export function findStashedContact(notes) {
    const match = LINE.exec(notes ?? '');
    if (!match) return null;
    const [line, stashedOn, phone, email] = match;
    return {
        phone: phone === NONE ? '' : phone,
        email: email === NONE ? '' : email,
        stashedOn,
        line,
    };
}

/**
 * The notes with the stashed line removed — what is left once the real contact is
 * back in the fields. Other notes are untouched; an empty result is '' not null, so
 * the caller can send it and clear the column.
 *
 * @param {string|null|undefined} notes
 * @returns {string}
 */
export function removeStashedContact(notes) {
    const found = findStashedContact(notes);
    if (!found) return notes ?? '';
    return (notes ?? '')
        .split('\n')
        .filter((l) => l !== found.line)
        .join('\n')
        .trim();
}
